import {useState} from 'react';
import {useIntl} from 'react-intl';
import {useQueryClient} from 'react-query';
import {Button as BootstrapButton, Card, Form, Modal} from 'react-bootstrap';
import ActionIcon from '../../../../../Components/ActionIcon';
import Button from '../../../../../Components/Button';
import {sonsQueryKey, useGetSons} from '../../../Hooks/TelephonieManager';
import Loading from '../../../../../app/common/components/Loading';
import {FichierSon, TypeSon} from '../TrunkSip/mocks';

/**
 * MAQUETTE — reproduction du composant partagé « ManageSound » : liste + formulaire
 * d'ajout de fichiers son, paramétré par type (musique d'attente, tonalité, annonce
 * fichier, TTS), comme le user control legacy réutilisé par Mélodies/Annonces/IVR.
 */

const QUOTAS: Record<TypeSon, number> = {
  musique_attente: 10,
  tonalite: 10,
  annonce_fichier: 100,
  tts: 100,
};

const LANGUES_TTS = [
  {value: 'fr', labelId: 'TELEPHONIE.PBX.SONS.TTS.LANGUE.FR'},
  {value: 'de', labelId: 'TELEPHONIE.PBX.SONS.TTS.LANGUE.DE'},
  {value: 'en', labelId: 'TELEPHONIE.PBX.SONS.TTS.LANGUE.EN'},
];

interface GestionSonsProps {
  type: TypeSon;
  titreId: string;
  descriptionId: string;
}

const GestionSons = ({type, titreId, descriptionId}: GestionSonsProps): JSX.Element | null => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const {data: sons, isLoading} = useGetSons(type);
  const [afficherAjout, setAfficherAjout] = useState(false);
  const [titre, setTitre] = useState('');
  const [fichierNom, setFichierNom] = useState('');
  const [langueTts, setLangueTts] = useState('fr');
  const [voixTts, setVoixTts] = useState<'homme' | 'femme'>('femme');
  const [texteTts, setTexteTts] = useState('');
  const [sonASupprimer, setSonASupprimer] = useState<FichierSon | null>(null);
  const [enCoursId, setEnCoursId] = useState<number | null>(null);

  if (isLoading || !sons) {
    return <Loading className="col-md-6" />;
  }

  const estTts = type === 'tts';
  const estFichier = type === 'musique_attente' || type === 'tonalite' || type === 'annonce_fichier';

  const mettreAJour = (sonsMisAJour: FichierSon[]): void => {
    queryClient.setQueryData(sonsQueryKey(type), sonsMisAJour);
  };

  const jouerApercu = (id: number): void => {
    try {
      const AudioContextClasse =
        window.AudioContext ||
        (window as unknown as {webkitAudioContext: typeof AudioContext}).webkitAudioContext;
      const contexte = new AudioContextClasse();
      const oscillateur = contexte.createOscillator();
      const gain = contexte.createGain();
      oscillateur.type = 'sine';
      oscillateur.frequency.value = 440;
      oscillateur.connect(gain);
      gain.connect(contexte.destination);
      gain.gain.setValueAtTime(0.1, contexte.currentTime);
      oscillateur.start();
      oscillateur.stop(contexte.currentTime + 0.3);
    } catch {
      // Web Audio API indisponible — pas d'aperçu sonore possible.
    }
    setEnCoursId(id);
    window.setTimeout(() => setEnCoursId(null), 300);
  };

  const activerSon = (id: number): void => {
    mettreAJour(sons.map((son) => ({...son, actif: son.id === id})));
  };

  const supprimerSon = (): void => {
    if (sonASupprimer) {
      mettreAJour(sons.filter((son) => son.id !== sonASupprimer.id));
    }
    setSonASupprimer(null);
  };

  const reinitialiserFormulaire = (): void => {
    setTitre('');
    setFichierNom('');
    setLangueTts('fr');
    setVoixTts('femme');
    setTexteTts('');
    setAfficherAjout(false);
  };

  const ajouterSon = (): void => {
    if (titre.trim() === '' || (estFichier && fichierNom.trim() === '') || (estTts && texteTts.trim() === '')) {
      return;
    }
    const nouvelId = Math.max(0, ...sons.map((son) => son.id)) + 1;
    const nouveauSon: FichierSon = estTts
      ? {id: nouvelId, titre: titre.trim(), langue: langueTts, voix: voixTts, texte: texteTts.trim()}
      : {id: nouvelId, titre: titre.trim()};
    mettreAJour([...sons, nouveauSon]);
    reinitialiserFormulaire();
  };

  const quota = QUOTAS[type];
  const quotaAtteint = sons.length >= quota;

  return (
    <Card bg="light" className="mb-5">
      <Card.Body>
        <div className="fw-bold text-uppercase fs-6 mb-1">
          {intl.formatMessage({id: titreId})} ({sons.length}/{quota})
        </div>
        <p className="text-muted fs-7 mb-4">{intl.formatMessage({id: descriptionId})}</p>

        {sons.length === 0 && (
          <div className="text-muted fs-6 mb-4">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.SONS.AUCUN_SON'})}
          </div>
        )}

        {sons.map((son) => (
          <div
            key={son.id}
            className="d-flex align-items-center justify-content-between gap-3 border-bottom py-2"
          >
            <div className="d-flex align-items-center gap-3">
              {type === 'musique_attente' && (
                <Form.Check
                  type="radio"
                  checked={son.actif ?? false}
                  onChange={() => activerSon(son.id)}
                  title={intl.formatMessage({id: 'TELEPHONIE.PBX.SONS.ACTIVER'})}
                />
              )}
              <i
                className={`bi ${enCoursId === son.id ? 'bi-pause-fill' : 'bi-play-fill'} text-primary fs-4 cursor-pointer`}
                onClick={() => jouerApercu(son.id)}
              />
              <span className="fs-6">
                {son.parDefaut
                  ? intl.formatMessage({id: 'TELEPHONIE.PBX.SONS.PAR_DEFAUT'})
                  : son.titre}
              </span>
              {estTts && son.langue && (
                <span className="text-muted fs-7">
                  ({son.langue} — {son.voix})
                </span>
              )}
            </div>
            {!son.parDefaut && (
              <ActionIcon
                label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
                faIcon="fs-4 bi bi-trash text-danger"
                onClick={() => setSonASupprimer(son)}
              />
            )}
          </div>
        ))}

        {!quotaAtteint && (
          <div className="mt-4">
            {!afficherAjout ? (
              <Button
                label={intl.formatMessage({id: 'TELEPHONIE.PBX.SONS.AJOUTER'})}
                faIcon="bi bi-plus-lg fs-6 me-2"
                onClick={() => setAfficherAjout(true)}
              />
            ) : (
              <div>
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.SONS.TITRE_LABEL'})}
                </Form.Label>
                <Form.Control
                  className="mb-3"
                  style={{maxWidth: 320}}
                  value={titre}
                  onChange={(e) => setTitre(e.target.value)}
                />

                {estFichier && (
                  <>
                    <Form.Label className="fs-6">
                      {intl.formatMessage({id: 'TELEPHONIE.PBX.SONS.FICHIER_LABEL'})}
                    </Form.Label>
                    <Form.Control
                      className="mb-3"
                      type="file"
                      accept=".mp3,.wav"
                      style={{maxWidth: 320}}
                      onChange={(e) => {
                        const input = e.target as HTMLInputElement;
                        setFichierNom(input.files?.[0]?.name ?? '');
                      }}
                    />
                  </>
                )}

                {estTts && (
                  <>
                    <Form.Label className="fs-6">
                      {intl.formatMessage({id: 'TELEPHONIE.PBX.SONS.TTS.LANGUE_LABEL'})}
                    </Form.Label>
                    <Form.Select
                      className="mb-3"
                      style={{maxWidth: 200}}
                      value={langueTts}
                      onChange={(e) => setLangueTts(e.target.value)}
                    >
                      {LANGUES_TTS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {intl.formatMessage({id: option.labelId})}
                        </option>
                      ))}
                    </Form.Select>

                    <Form.Label className="fs-6">
                      {intl.formatMessage({id: 'TELEPHONIE.PBX.SONS.TTS.VOIX_LABEL'})}
                    </Form.Label>
                    <div className="d-flex gap-4 mb-3">
                      <Form.Check
                        type="radio"
                        id={`voix-homme-${type}`}
                        label={intl.formatMessage({id: 'TELEPHONIE.PBX.SONS.TTS.VOIX_HOMME'})}
                        checked={voixTts === 'homme'}
                        onChange={() => setVoixTts('homme')}
                      />
                      <Form.Check
                        type="radio"
                        id={`voix-femme-${type}`}
                        label={intl.formatMessage({id: 'TELEPHONIE.PBX.SONS.TTS.VOIX_FEMME'})}
                        checked={voixTts === 'femme'}
                        onChange={() => setVoixTts('femme')}
                      />
                    </div>

                    <Form.Label className="fs-6">
                      {intl.formatMessage({id: 'TELEPHONIE.PBX.SONS.TTS.TEXTE_LABEL'})}
                    </Form.Label>
                    <Form.Control
                      className="mb-3"
                      as="textarea"
                      rows={3}
                      maxLength={2000}
                      style={{maxWidth: 480}}
                      value={texteTts}
                      onChange={(e) => setTexteTts(e.target.value)}
                    />
                    <div className="text-muted fs-8 mb-3">{texteTts.length}/2000</div>
                  </>
                )}

                <div className="d-flex gap-3">
                  <BootstrapButton variant="secondary" size="sm" onClick={reinitialiserFormulaire}>
                    {intl.formatMessage({id: 'CANCEL'})}
                  </BootstrapButton>
                  <Button
                    label={intl.formatMessage({id: 'TELEPHONIE.PBX.SONS.AJOUTER'})}
                    onClick={ajouterSon}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Card.Body>

      <Modal show={sonASupprimer != null} onHide={() => setSonASupprimer(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {intl.formatMessage(
            {id: 'TELEPHONIE.PBX.SONS.DELETE_BODY'},
            {titre: sonASupprimer?.titre ?? ''}
          )}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setSonASupprimer(null)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={supprimerSon}>
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default GestionSons;
